import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import gsap from "gsap";

const COLORS = {
  primary: "#3AAFA9",
  secondary: "#7BC8A4",
  heading: "#2E2E2E",
  text: "#4B5563",
  sectionTitle: "#1F7A75",
  background: "#3AAFA9",
};

const policySections = [
  {
    title: "1. Introduction",
    text:
      "JANAK ELDERBRIDGE PRIVATE LIMITED (\"Janak\") values your privacy and digital trust. This Privacy Policy explains how we collect, use, store, and protect your information across our website and mobile applications (iOS & Android). We fully adhere to Google Play Store & Apple App Store review compliance rules, the Information Technology Act, 2000 (IT Act), and the Digital Personal Data Protection Act, 2023 (DPDP Act).",
  },
  {
    title: "2. Information & App Permissions We Collect",
    text: "To function as a dependable healthcare support and care coordination platform, our applications request specific data and system permissions:",
    list: [
      "Contact Information: Name, phone number, and email address for account creation and secure verification.",
      "Care & Medical Profile: Elder profile details, family contact numbers, and routine medical history provided for care team coordination.",
      "Location Data (When In Use): We use location services (via expo-location and React Native Maps) to auto-fill current addresses during sign-up and to accurately coordinate service addresses for scheduled care partner visits.",
      "Camera & Storage Permissions (Media): We request camera and photo file access (via expo-image-picker and manipulators) strictly for uploading user profile avatars, medical reports, prescriptions, or care partner service verifications.",
      "SMS & Push Notifications: We leverage Firebase Cloud Messaging (FCM) to deliver urgent service alerts, OTP authentication notifications, and service update notifications without accessing your private personal SMS inbox.",
      "Financial Data: Payments for subscriptions and elder care bookings are routed via secure, PCI-DSS compliant third-party gateways (e.g., Cashfree PG SDK). Janak does not directly store sensitive credit card digits or UPI credentials."
    ],
  },
  {
    title: "3. How We Use Your Information",
    list: [
      "To provide, schedule, and execute personalized elder care assistance and support partner dispatch",
      "To coordinate timely care assistance and real-time service updates",
      "To maintain platform security, authentic sign-in verification, and seamless communication",
      "To notify families regarding vital updates, service order milestones, and safety notices",
    ],
  },
  {
    title: "4. Data Sharing & Third Parties",
    text:
      "Janak never sells or rents personal user data. Information is shared strictly on a verified need-to-know basis with assigned healthcare service partners, vetted infrastructure providers (Firebase authentication, AWS hosting, and Cashfree payment systems), or government regulatory authorities when lawfully mandated.",
  },
  {
    title: "5. Data Security & Encryption",
    text:
      "We apply state-of-the-art administrative, technical, and cryptographic safeguards (including end-to-end TLS/SSL transmission and at-rest encryption) to prevent unauthorized access, tampering, data loss, or unlawful interception.",
  },
  {
    title: "6. Data Retention & Account Deletion Policy",
    text: (
      <span>
        In compliance with Apple Guideline 5.1.1(v) and Google Play Data Safety standards, you hold full ownership of your account lifecycle. We retain personal records only as long as an active service relationship exists. You have the unconditional right to request permanent account and data erasure directly within the mobile app (Profile -&gt; Delete Account) or online via our public Account Deletion page at{" "}
        <Link to="/account-deletion" className="text-[#3AAFA9] hover:underline font-medium">
          /account-deletion
        </Link>.
      </span>
    ),
  },
  {
    title: "7. User Rights (Under DPDP Act)",
    list: [
      "Right to access your collected personal details and operational transparency",
      "Right to rectification of inaccurate, outdated, or incomplete care profile records",
      "Right to complete account erasure and withdrawal of digital consent at any time",
      "Right to nominate a trusted representative in instances of elder incapacity",
    ],
  },
  {
    title: "8. Cookies and Analytics",
    text:
      "Janak may use cookies and analytics tools to understand platform performance and improve usability. You can manage cookie settings through your device or browser.",
  },
  {
    title: "9. Children's Privacy",
    text:
      "Janak is not intended for independent use by children. Accounts must be created by authorized adults. We do not knowingly collect data from children in violation of applicable laws.",
  },
  {
    title: "10. Grievance Officer",
    text:
      "In accordance with the IT Act, 2000 and DPDP Act, 2023, the contact details of our Grievance Officer are provided below. Please contact us for any privacy-related grievances.",
  },
  {
    title: "11. Policy Updates",
    text:
      "We may update this Privacy Policy periodically. Continued use of Janak after updates indicates acceptance of the revised policy.",
  },
  {
    title: "12. Contact Us",
    text: (
      <span>
        For privacy questions, data requests, or grievances, contact us at{" "}
        <a href="mailto:contact@thejanak.com" className="text-[#3AAFA9] hover:underline font-medium">
          contact@thejanak.com
        </a>{" "}
        or call{" "}
        <a href="tel:+919098114568" className="text-[#3AAFA9] hover:underline font-medium">
          +91 90981 14568
        </a>.
      </span>
    ),
  },
];

const PrivacyPolicy = () => {
  const pageRef = useRef(null);
  const cardRef = useRef(null);
  const headingRef = useRef(null);
  const introRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power2.out" } });
      tl.from(pageRef.current, { opacity: 0, duration: 0.5 })
        .from(cardRef.current, { y: 36, opacity: 0, duration: 0.7 }, "-=0.2")
        .from(headingRef.current, { y: 18, opacity: 0, duration: 0.45 }, "-=0.2")
        .from(introRef.current, { y: 16, opacity: 0, duration: 0.45 }, "-=0.25")
        .from(
          ".policy-section-item",
          { y: 24, opacity: 0, stagger: 0.07, duration: 0.38 },
          "-=0.2",
        );
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={pageRef}
      className="relative min-h-screen overflow-hidden px-6 py-20"
      style={{ background: COLORS.background }}
    >
      <div
        className="absolute left-10 top-24 h-72 w-72 rounded-full blur-3xl"
        style={{ background: `${COLORS.primary}33` }}
      />
      <div
        className="absolute bottom-20 right-10 h-72 w-72 rounded-full blur-3xl"
        style={{ background: `${COLORS.secondary}40` }}
      />

      <div
        ref={cardRef}
        className="relative mx-auto max-w-4xl rounded-3xl bg-white/80 px-6 py-12 text-gray-800 shadow-[0_30px_90px_rgba(0,0,0,0.15)] backdrop-blur-2xl md:px-12"
      >
        <h1
          ref={headingRef}
          className="mb-10 text-center text-4xl font-extrabold md:text-5xl"
          style={{ color: COLORS.heading }}
        >
          Privacy Policy
        </h1>

        <p
          ref={introRef}
          className="mb-10 text-center text-base leading-relaxed md:text-lg"
          style={{ color: COLORS.text }}
        >
          This policy explains how{" "}
          <span className="font-semibold" style={{ color: COLORS.primary }}>
            Janak
          </span>{" "}
          collects and protects your information while providing safe and reliable care support.
        </p>

        <div className="space-y-9 text-[15.5px] leading-relaxed">
          {policySections.map((section) => (
            <div key={section.title} className="policy-section-item">
              <h2
                className="mb-3 text-xl font-bold md:text-2xl"
                style={{ color: COLORS.sectionTitle }}
              >
                {section.title}
              </h2>
              {section.text ? <p style={{ color: COLORS.text }}>{section.text}</p> : null}
              {section.list ? (
                <ul className="list-disc space-y-1.5 pl-6" style={{ color: COLORS.text }}>
                  {section.list.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ))}
        </div>

        <p className="mt-14 text-center text-sm text-gray-500">Last updated: July 2026</p>
      </div>
    </section>
  );
};

export default PrivacyPolicy;
